import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError } from '@/api/client';
import { referralApi } from '@/api/referral';
import { walletApi } from '@/api/wallet';
import { useAuthStore } from '@/store/auth';
import { useWalletStore } from '@/store/wallet';
import {
  ConviteCta,
  ConviteGhost,
  ConviteSheetView,
  SheetFrame
} from '@/components/referral/ConviteViews';
import { GiftOutline } from '@/components/referral/icons';
import { formatBRL } from '@/lib/format';
import { friendlyMessage } from '@/lib/errors';
import { clearPendingReferralCode, getPendingReferralCode } from '@/lib/referral';

/**
 * Aplica o código de indicação PENDENTE (landing `/convite`, QR escaneado ou
 * campo do cadastro) assim que há sessão com perfil completo. Montado no
 * AppLayout, ao lado do LevelChangeToast — um só lugar cobre email, Google e
 * Apple sem tocar nos endpoints de auth.
 *
 * DOIS PASSOS (auditoria 2026-08-22): (1) lookup público do código → sheet
 * "Convite de {nome} — R$ X de cashback no primeiro aporte" (design "Bow
 * Convite do Indicado", variante corrigida: lockup oficial, presente navy +
 * verde, chip do código, CTA verde "Aceitar convite"); (2) só com o toque em
 * "Aceitar convite" é que `POST /referral/claim` roda. Sem isso, um adesivo QR
 * `bow.app.br/convite/ATAC-XXX` colado no carregador virava o atacante
 * "indicador" de quem escaneasse logado, sem nenhuma pergunta.
 *
 * Resultado vira sheet: sucesso ("faça seu 1º aporte e ganhe R$ X") ou aviso
 * (não elegível / inválido). Erros que NÃO são veredito — 403
 * profile_incomplete, 503 programa desligado, 429, rede — mantêm o pendente
 * pra tentar de novo (TTL de 14 d no storage). "Agora não" descarta o pendente.
 */

type Step =
  | { kind: 'confirm'; code: string; referrerFirstName: string; welcomeBrl: string }
  | { kind: 'ok'; welcomeBrl: string; referrerFirstName: string }
  | { kind: 'notice'; title: string; message: string; offerIndique: boolean };

// Guard de módulo contra o double-effect do StrictMode e remounts do layout.
let inflight = false;

const DISPLAY = 'Chillax, system-ui, sans-serif';

export function ReferralClaimer() {
  const user = useAuthStore((s) => s.user);
  const setWallet = useWalletStore((s) => s.setWallet);
  const navigate = useNavigate();
  const [step, setStep] = useState<Step | null>(null);
  const [busy, setBusy] = useState(false);

  // Passo 1 — descobre de quem é o código e PERGUNTA.
  useEffect(() => {
    if (!user || user.profile_complete === false) return;
    const code = getPendingReferralCode();
    if (!code || inflight) return;
    inflight = true;
    referralApi
      .lookup(code)
      .then((info) => {
        if (!info.valid) {
          // Código que não existe: descarta sem incomodar.
          clearPendingReferralCode();
          return;
        }
        setStep({
          kind: 'confirm',
          code,
          referrerFirstName: info.referrer_first_name ?? 'Alguém',
          welcomeBrl: info.welcome_brl ?? '0'
        });
      })
      .catch(() => {
        // 503 (programa desligado) / rede — mantém o pendente pra próxima abertura.
      })
      .finally(() => {
        inflight = false;
      });
  }, [user]);

  function close() {
    setStep(null);
  }

  function decline() {
    clearPendingReferralCode();
    close();
  }

  // Passo 2 — só depois do toque em "Aceitar convite".
  async function confirm() {
    if (!step || step.kind !== 'confirm' || busy) return;
    setBusy(true);
    try {
      const res = await referralApi.claim(step.code);
      clearPendingReferralCode();
      setStep({
        kind: 'ok',
        welcomeBrl: res.welcome_brl,
        referrerFirstName: res.referrer_first_name
      });
      walletApi
        .get()
        .then(setWallet)
        .catch(() => {
          /* best-effort */
        });
    } catch (err) {
      if (err instanceof ApiError) {
        // Sem veredito ainda: perfil incompleto, programa desligado, rate limit
        // → mantém o pendente e fecha; tenta na próxima abertura.
        if (err.status === 403 || err.status === 503 || err.status === 429) {
          close();
          return;
        }
        clearPendingReferralCode();
        const notEligible = err.code === 'referral_not_eligible';
        setStep({
          kind: 'notice',
          title: notEligible ? 'Você já é da Bow' : 'Código de indicação',
          message: err.message || friendlyMessage(err, 'Não foi possível aplicar o código.'),
          offerIndique: notEligible || err.code === 'referral_already_claimed'
        });
        return;
      }
      // Rede/offline — mantém o pendente, fecha.
      close();
    } finally {
      setBusy(false);
    }
  }

  if (!step) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center"
      // Tela atual atrás, desfocada (mock: mapa com blur 7px) — sem véu escuro
      // pesado; o sheet branco com sombra pra cima já separa os planos.
      style={{
        background: 'rgba(0,27,116,0.18)',
        backdropFilter: 'blur(7px)',
        WebkitBackdropFilter: 'blur(7px)'
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) close();
      }}
    >
      {step.kind === 'confirm' && (
        <ConviteSheetView
          referrerFirstName={step.referrerFirstName}
          code={step.code}
          welcomeBrl={step.welcomeBrl}
          busy={busy}
          onAccept={() => void confirm()}
          onDecline={decline}
        />
      )}

      {step.kind === 'ok' && (
        <SheetFrame>
          <div className="mt-2 flex justify-center">
            <GiftOutline size={62} sparks />
          </div>
          <div className="mt-5 text-center">
            <div
              className="text-[11.5px] font-semibold uppercase tracking-[0.09em]"
              style={{ color: 'rgba(0,27,116,0.5)' }}
            >
              Convite de {step.referrerFirstName}
            </div>
            <div
              className="mt-[7px] text-bow-royal"
              style={{
                fontFamily: DISPLAY,
                fontWeight: 600,
                fontSize: 31,
                letterSpacing: '-0.03em',
                lineHeight: 1.05
              }}
            >
              Código aplicado!
            </div>
            <p
              className="mx-auto mt-3 max-w-[300px] text-[15px] leading-relaxed"
              style={{ color: 'rgba(0,27,116,0.65)' }}
            >
              Faça seu primeiro aporte de saldo (PIX ou cartão) e ganhe{' '}
              <strong className="font-semibold text-bow-royal">
                {formatBRL(step.welcomeBrl)}
              </strong>{' '}
              de cashback pra usar na recarga.
            </p>
          </div>
          <div className="mt-[22px] flex flex-col gap-1">
            <ConviteCta
              onClick={() => {
                close();
                navigate('/wallet/recharge');
              }}
            >
              Adicionar saldo
            </ConviteCta>
            <ConviteGhost onClick={close}>Depois</ConviteGhost>
          </div>
        </SheetFrame>
      )}

      {step.kind === 'notice' && (
        <SheetFrame>
          <div className="mt-2 flex justify-center">
            <GiftOutline size={62} />
          </div>
          <div className="mt-5 text-center">
            <div
              className="text-[11.5px] font-semibold uppercase tracking-[0.09em]"
              style={{ color: 'rgba(0,27,116,0.5)' }}
            >
              Indique e ganhe
            </div>
            <div
              className="mt-[7px] text-bow-royal"
              style={{
                fontFamily: DISPLAY,
                fontWeight: 600,
                fontSize: 27,
                letterSpacing: '-0.03em',
                lineHeight: 1.05
              }}
            >
              {step.title}
            </div>
            <p
              className="mx-auto mt-3 max-w-[300px] text-[15px] leading-relaxed"
              style={{ color: 'rgba(0,27,116,0.65)' }}
            >
              {step.message}
            </p>
          </div>
          <div className="mt-[22px] flex flex-col gap-1">
            {step.offerIndique ? (
              <>
                <ConviteCta
                  onClick={() => {
                    close();
                    navigate('/indique');
                  }}
                >
                  Indicar meus amigos
                </ConviteCta>
                <ConviteGhost onClick={close}>Fechar</ConviteGhost>
              </>
            ) : (
              <ConviteCta onClick={close}>Entendi</ConviteCta>
            )}
          </div>
        </SheetFrame>
      )}
    </div>
  );
}
